/** State-only stand inspector renderer owned by the sealed map workspace. */
import React from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { FmButton } from './FmButton';
import { FmText } from './FmComposition';
import { FmValueList, type FmValueListItem } from './FmValueList';
import { FmRowActions, type FmRowActionConfirm } from './FmRowActions';
import './FmRightInspector.css';
import { fmMapOcclusion } from '@/shared/ui/mapOcclusion';

export interface FmRightInspectorValueDescriptor {
  id: string;
  label: string;
  value: string;
  unavailable: boolean;
}

export interface FmRightInspectorDescriptor {
  ariaLabel: string;
  title: string;
  closeLabel: string;
  openFullLabel?: string;
  values: readonly FmRightInspectorValueDescriptor[];
  actions?: readonly {
    id: string;
    label: string;
    intent: 'primary' | 'secondary' | 'inspect' | 'destructive';
    disabledReason?: string;
    confirm?: FmRowActionConfirm;
  }[];
  hazards?: {
    ariaLabel: string;
    eyebrow: string;
    items: readonly { id: string; label: string }[];
  };
}

export type FmRightInspectorEvent =
  | { kind: 'close' }
  | { kind: 'open-full' }
  | { kind: 'action'; actionId: string };

export interface FmRightInspectorProps {
  descriptor: FmRightInspectorDescriptor;
  onEvent: (event: FmRightInspectorEvent) => void;
}

function valueItems(values: readonly FmRightInspectorValueDescriptor[]): FmValueListItem[] {
  return values.map((item) => ({
    id: item.id,
    label: item.label,
    value: item.value,
    unavailable: item.unavailable,
  }));
}

const FmRightInspector: React.FC<FmRightInspectorProps> = ({ descriptor, onEvent }) => (
  <aside
    className="fm-right-inspector"
    data-testid="fm-right-inspector"
    {...fmMapOcclusion('panel', 'panel')}
    aria-label={descriptor.ariaLabel}
  >
    <div className="fm-right-inspector__header">
      <FmText as="span" className="fm-right-inspector__title">
        {descriptor.title}
      </FmText>
      <FmButton
        variant="text"
        className="fm-right-inspector__close-btn"
        data-testid="fm-inspector-close-btn"
        onClick={() => onEvent({ kind: 'close' })}
        aria-label={descriptor.closeLabel}
        leadingIcon={<CloseOutlined />}
      />
    </div>

    <div className="fm-right-inspector__body" data-testid="fm-inspector-body">
      <FmValueList
        items={valueItems(descriptor.values)}
        columns="two"
        density="comfortable"
        className="fm-right-inspector__props"
      />
      {descriptor.hazards ? (
        <section
          className="fm-right-inspector__hazards"
          data-testid="fm-inspector-hazards"
          aria-label={descriptor.hazards.ariaLabel}
        >
          <FmText size="fine" eyebrow tone="muted">
            {descriptor.hazards.eyebrow}
          </FmText>
          {descriptor.hazards.items.map((item) => (
            <FmText key={item.id} size="fine">
              {item.label}
            </FmText>
          ))}
        </section>
      ) : null}
    </div>

    <div className="fm-right-inspector__footer">
      {descriptor.actions?.length ? (
        <FmRowActions
          actions={descriptor.actions.map((action) => ({
            ...action,
            onSelect: () =>
              onEvent({
                kind: 'action',
                actionId: action.id,
              }),
          }))}
          policy="primary-plus-menu"
          label={descriptor.title}
          overflowLabel={descriptor.title}
          data-testid="fm-inspector-actions"
        />
      ) : null}
      {descriptor.openFullLabel ? (
        <FmButton
          variant="primary"
          className="fm-right-inspector__open-full-btn"
          data-testid="fm-inspector-open-full"
          onClick={() => onEvent({ kind: 'open-full' })}
          aria-label={descriptor.openFullLabel}
        >
          {descriptor.openFullLabel}
        </FmButton>
      ) : null}
    </div>
  </aside>
);

FmRightInspector.displayName = 'FmRightInspector';

export default FmRightInspector;
